import React, { useState, useEffect } from 'react';
import { Loader2, MapPin, BarChart3, Locate } from 'lucide-react';
import './ZipcodeHeatmap.css';
import { ZipcodeHeatmapProps, HeatmapConfig, MetricType, ZipcodeMetrics } from './types';
import { calculateColorScale } from './utils';
import ZipcodeHeatmapCore from './ZipcodeHeatmapCore';
import MetricLegend from './MetricLegend';
import { useGetZipcodeGeocodeQuery } from '../../endpoints/zipcode.endpoints';

const defaultConfig: HeatmapConfig = {
  selectedMetric: 'clicks',
  colorScheme: 'blue',
  showLegend: true,
  showTooltips: true,
  mapHeight: 400,
  minOpacity: 0.3,
  maxOpacity: 0.9,
};

const ZipcodeHeatmapDisplay: React.FC<ZipcodeHeatmapProps> = ({
  data,
  config: userConfig = {},
  onZipcodeClick,
  className = '',
  isLoading = false,
}) => {
  const [processedData, setProcessedData] = useState<ZipcodeMetrics[]>([]);
  const [config, setConfig] = useState<HeatmapConfig>({ ...defaultConfig, ...userConfig });
  const [isColumn2Collapsed, setIsColumn2Collapsed] = useState(false);

  const { data: geocodeData, isLoading: isGeocodeLoading, isFetching: isGeocodeFetching, refetch } = useGetZipcodeGeocodeQuery({ zipcodes: data.map(d => d.zipcode) }, { skip: !data.length });

  // Process zipcode data to include coordinates
  useEffect(() => {
    if (data.length > 0 && !isGeocodeLoading) {
      refetch();
    }

    if (geocodeData) {
      setProcessedData(geocodeData.map(d => ({
        ...data.find(z => z.zipcode === d.zipcode)!,
        lat: d.geocode.lat,
        lng: d.geocode.lng,
        city: d.geocode.city,
        state: d.geocode.state,
      })));
    }

  }, [data, geocodeData]);

  // Update config when userConfig changes
  useEffect(() => {
    setConfig({ ...defaultConfig, ...userConfig });
  }, [userConfig]);

  // Get available metrics from the data
  const getAvailableMetrics = (): MetricType[] => {
    const baseMetrics: MetricType[] = ['clicks', 'impressions', 'spend', 'conversions'];
    const calculatedMetrics: MetricType[] = [];

    // Check if we have enough data for calculated metrics
    const hasClicksAndImpressions = processedData.some(d => d.clicks > 0 && d.impressions > 0);
    const hasSpendAndClicks = processedData.some(d => d.spend > 0 && d.clicks > 0);
    const hasSpendAndConversions = processedData.some(d => d.spend > 0 && d.conversions > 0);

    if (hasClicksAndImpressions) calculatedMetrics.push('ctr');
    if (hasSpendAndClicks) calculatedMetrics.push('cpc');
    if (hasSpendAndConversions) calculatedMetrics.push('cpa');

    return [...baseMetrics, ...calculatedMetrics];
  };

  const handleMetricChange = (metric: MetricType) => {
    setConfig(prev => ({ ...prev, selectedMetric: metric }));
  };

  const colorScale = processedData.length > 0
    ? calculateColorScale(processedData, config.selectedMetric)
    : { min: 0, max: 0, colors: [] };

  const isDataLoading = isLoading || isGeocodeLoading || isGeocodeFetching;

  if (isDataLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-8 ${className}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Loading Zipcode Heatmap
          </h3>
          <p className="text-sm text-gray-600 text-center">
            Processing zipcode data and fetching geographic coordinates...
          </p>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-8 ${className}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <MapPin className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Zipcode Data
          </h3>
          <p className="text-sm text-gray-600 text-center">
            No zipcode metrics data available to display on the heatmap.
          </p>
        </div>
      </div>
    );
  }

  if (!processedData.length) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-8 ${className}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <BarChart3 className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Geographic Data Unavailable
          </h3>
          <p className="text-sm text-gray-600 text-center">
            Could not determine coordinates for the provided zipcodes.
            <br />
            Please ensure zipcodes are valid US postal codes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
        <div className="flex justify-between bg-gray-201">
          <div className='flex items-center'>
            <div className='flex gap-2 p-2 pl-4 items-center font-medium'>
              <Locate className="w-4 h-4 text-black" />
              Location
            </div>
            {/* <div className='border-l border-gray-401 h-1/2 ml-4 mr-2' />
            <div className='flex items-center'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="med"
                  >
                    <ChevronDown className="w-4 h-4" />
                    Impressions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='z-[999]'>
                  <DropdownMenuItem>
                    Clicks
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    Impressions
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    Spend
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div> */}
          </div>
          <div className='flex gap-2 p-2 pr-4 items-center font-normal text-sm'>
            Showing
            {' '}
            {processedData.length}
            {' '}
            zipcode
            {processedData.length !== 1 ? 's' : ''}
            {' '}
          </div>
        </div>

        <div className="flex flex-col gap-4 w-full">
          <div className="w-full relative">
            <ZipcodeHeatmapCore
              data={processedData}
              config={config}
              onZipcodeClick={onZipcodeClick}
              className="w-full"
            />
            <div className='absolute top-5 right-5 z-[999]'>
              <MetricLegend
                colorScale={colorScale}
                metric={config.selectedMetric}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ZipcodeHeatmapDisplay;
